const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

// Simple API helper for conversation service
const apiHelper = {
  async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = localStorage.getItem('token');
    
    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(url, defaultOptions);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.error || errorData?.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  get<T>(endpoint: string): Promise<T> {
    return this.makeRequest(endpoint);
  },

  post<T>(endpoint: string, data?: any): Promise<T> {
    return this.makeRequest(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    });
  }
};

export interface Conversation {
  id: string;
  conversationId: string;
  contact: {
    name: string;
    email: string;
    company?: string;
  };
  lastMessage: {
    content: string;
    timestamp: string;
    isOutgoing: boolean;
  };
  status: string;
  priority: string;
  campaign: string;
  messageCount: number;
  unreadCount: number;
  labels: string[];
  metadata: any;
  channel: string;
  workflowId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ConversationMessage {
  id: string;
  content: string;
  isOutgoing: boolean;
  timestamp: string;
  channel: string;
  messageType: string;
  workflowId?: string;
  metadata: any;
}

export interface ConversationDetails {
  id: string;
  conversationId: string;
  contact: {
    name: string;
    email: string;
    company?: string;
  };
  status: string;
  priority: string;
  campaign: string;
  messageCount: number;
  unreadCount: number;
  labels: string[];
  channel: string;
  workflowId?: string;
  messages: ConversationMessage[];
  metadata: any;
  createdAt: string;
  updatedAt: string;
}

export interface ConversationStats {
  totalConversations: number;
  activeConversations: number;
  highPriority: number;
  avgResponseTime: string;
  responseRate: number;
  positiveReplies: number;
}

export interface AIResponse {
  id: string;
  content: string;
  subject?: string;
  confidence: number;
  reasoning: string;
  alternatives: string[];
  metadata: {
    generation_time: number;
    model_version: string;
    processing_steps: string[];
    trace_id: string;
  };
}

export interface SendReplyRequest {
  content: string;
  subject?: string;
  attachments?: File[];
  ai_generated?: boolean;
  ai_confidence?: number;
}

export interface SearchFilters {
  search?: string;
  status?: string;
  channel?: string;
  campaign_id?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface ConversationSearchResult {
  conversations: Conversation[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  stats: {
    totalConversations: number;
    activeConversations: number;
    highPriority: number;
    needsResponse: number;
  };
}

export interface RevolutionaryConversationDetails {
  id: string;
  contact: {
    name?: string;
    email: string;
    company?: string;
  };
  status: string;
  priority: string;
  purpose?: {
    type: string;
    description?: string;
    targetOutcome?: string;
  };
  messages: Array<{
    id: string;
    content: string;
    direction: 'inbound' | 'outbound';
    timestamp: Date;
    source: 'human' | 'ai' | 'workflow';
    aiGenerated?: boolean;
    humanEdited?: boolean;
    aiConfidence?: number;
    workflowExecutionId?: string;
    attachments?: any[];
    metadata?: object;
  }>;
  workflowExecutions: any[];
  aiInsights?: {
    sentiment: any;
    intent: any;
    urgency: string;
    nextBestAction: string;
    keyTopics: string[];
  };
  metadata: object;
  traceId: string;
}

class ConversationService {
  private baseUrl = '/api';

  /**
   * Get all conversations with filtering and pagination
   */
  async getConversations(filters: any = {}): Promise<any> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response: any = await apiHelper.get(`${this.baseUrl}/conversations?${params}`);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch conversations');
    }

    return response.data;
  }

  /**
   * Get conversation stats
   */
  async getConversationStats(): Promise<ConversationStats> {
    const response: any = await apiHelper.get(`${this.baseUrl}/conversations/stats`);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch conversation stats');
    }

    return response.data;
  }

  /**
   * Get conversation by ID with messages
   */
  async getConversation(conversationId: string): Promise<ConversationDetails> {
    const response: any = await apiHelper.get(`${this.baseUrl}/conversations/${conversationId}`);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch conversation');
    }

    return response.data;
  }

  /**
   * Get messages for a conversation
   */
  async getConversationMessages(conversationId: string, page = 1, limit = 50): Promise<{
    conversationId: string;
    messages: ConversationMessage[];
    messageCount: number;
  }> {
    const response: any = await apiHelper.get(
      `${this.baseUrl}/conversations/${conversationId}/messages?page=${page}&limit=${limit}`
    );
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch conversation messages');
    }

    return response.data;
  }

  /**
   * Get revolutionary conversation details with AI insights
   */
  async getRevolutionaryConversation(conversationId: string): Promise<any> {
    const response: any = await apiHelper.get(`${this.baseUrl}/conversations/revolutionary/${conversationId}`);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch revolutionary conversation details');
    }

    return response.data;
  }



  /**
   * Send reply to conversation
   */
  async sendReply(conversationId: string, replyData: any): Promise<any> {
    const formData = new FormData();
    formData.append('content', replyData.content);
    
    if (replyData.subject) {
      formData.append('subject', replyData.subject);
    }
    
    if (replyData.ai_generated) {
      formData.append('ai_generated', 'true');
    }
    
    if (replyData.ai_confidence) {
      formData.append('ai_confidence', replyData.ai_confidence.toString());
    }
    
    if (replyData.attachments && replyData.attachments.length > 0) {
      replyData.attachments.forEach((file: any) => {
        formData.append('attachments', file);
      });
    }

    const response = await fetch(`${API_BASE_URL}${this.baseUrl}/conversations/revolutionary/${conversationId}/reply`, {
      method: 'POST',
      body: formData,
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
      }
    });

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to send reply');
    }

    return result;
  }

  /**
   * Set conversation purpose for AI guidance
   */
  async setConversationPurpose(
    conversationId: string,
    purposeType: string,
    description?: string,
    targetOutcome?: string
  ): Promise<{
    success: boolean;
    data: any;
    traceId: string;
  }> {
    const response: any = await apiHelper.post(`${this.baseUrl}/conversations/${conversationId}/purpose`, {
      purpose_type: purposeType,
      description,
      target_outcome: targetOutcome
    });
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to set conversation purpose');
    }

    return response;
  }

  /**
   * Get AI analysis for conversation
   */
  async getAIAnalysis(conversationId: string): Promise<any> {
    const response: any = await apiHelper.post(`${this.baseUrl}/conversations/revolutionary/${conversationId}/ai/analyze`);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to get AI analysis');
    }

    return response.data;
  }

  /**
   * Search conversations with advanced filters
   */
  async searchConversations(query: any): Promise<any> {
    const startTime = performance.now();
    const searchParams = new URLSearchParams();
    
    // Always include pagination
    if (query.page) {
      searchParams.append('page', query.page.toString());
    }
    
    if (query.limit) {
      searchParams.append('limit', query.limit.toString());
    }
    
    // Add search query if provided
    if (query.query && query.query.trim()) {
      searchParams.append('search', query.query.trim());
    }
    
    // Add filters if provided
    if (query.filters && query.filters.filters && query.filters.filters.length > 0) {
      // TODO: Implement advanced filtering
      console.log('Advanced filters:', query.filters);
    }

    const response = await this.getConversations(Object.fromEntries(searchParams));
    const searchTime = performance.now() - startTime;
    
    return {
      conversations: response.conversations,
      page: response.pagination.page,
      limit: response.pagination.limit,
      total: response.pagination.total,
      hasMore: response.pagination.page < response.pagination.pages,
      facets: query.includeFacets ? {} : undefined,
      searchTime: Math.round(searchTime)
    };
  }

  /**
   * Get dashboard overview with AI metrics
   */
  async getDashboardOverview(): Promise<{
    stats: {
      aiHandledPercentage: number;
      totalConversations: number;
      activeConversations: number;
      needsAttention: number;
      avgResponseTime: string;
      responseRate: number;
      positiveReplies: number;
    };
    aiMetrics: {
      confidence: number;
      processingQueue: number;
      escalations: number;
      responseAccuracy: number;
      learningRate: string;
      lastUpdate: string;
    };
    recentActivity: Array<{
      type: string;
      conversationId: string;
      timestamp: string;
      confidence?: number;
      outcome?: string;
    }>;
  }> {
    const response: any = await apiHelper.get(`${this.baseUrl}/conversations/management/dashboard/overview`);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch dashboard overview');
    }

    return response.data;
  }

  /**
   * Get AI performance metrics
   */
  async getAIMetrics(): Promise<{
    performance: {
      responseAccuracy: number;
      avgConfidence: number;
      processingSpeed: number;
      learningRate: number;
    };
    usage: {
      totalResponses: number;
      humanOverrides: number;
      escalations: number;
      feedbackCount: number;
    };
    trends: {
      daily: number[];
      weekly: number[];
    };
  }> {
    const response: any = await apiHelper.get(`${this.baseUrl}/ai/metrics`);
    
    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch AI metrics');
    }

    return response.data;
  }

  /**
   * Enhanced conversation service methods for improved conversations page
   */

  /**
   * Send a message in a conversation with file upload support
   */
  async sendConversationMessage(conversationId: string, data: {
    content: string;
    subject?: string;
    attachments?: File[];
    aiGenerated?: boolean;
    aiConfidence?: number;
  }): Promise<any> {
    const formData = new FormData();
    formData.append('content', data.content);
    if (data.subject) formData.append('subject', data.subject);
    if (data.aiGenerated) formData.append('ai_generated', 'true');
    if (data.aiConfidence) formData.append('ai_confidence', data.aiConfidence.toString());
    
    // Add attachments
    if (data.attachments) {
      data.attachments.forEach((file, index) => {
        formData.append(`attachments`, file);
      });
    }

    const response = await fetch(`${API_BASE_URL}${this.baseUrl}/conversations/revolutionary/${conversationId}/reply`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}` || ''
      },
      body: formData
    });

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to send message');
    }

    return result.data;
  }

  /**
   * Generate AI response for a conversation
   */
  async generateAIResponse(conversationId: string, options: {
    context?: any;
    tone?: string;
    urgency?: string;
  } = {}): Promise<any> {
    const response = await fetch(`${API_BASE_URL}${this.baseUrl}/conversations/revolutionary/${conversationId}/ai/generate-response`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}` || ''
      },
      body: JSON.stringify({
        context: options.context,
        preferences: {
          tone: options.tone || 'professional'
        },
        business_context: {
          priority: options.urgency || 'medium'
        }
      })
    });

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to generate AI response');
    }

    return result.data;
  }

  /**
   * Upload files for a conversation
   */
  async uploadConversationFiles(conversationId: string, files: File[]): Promise<any> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    formData.append('conversationId', conversationId);

    const response = await fetch(`${API_BASE_URL}${this.baseUrl}/conversations/${conversationId}/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}` || ''
      },
      body: formData
    });

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to upload files');
    }

    return result.data;
  }

  /**
   * Get enhanced conversation details with AI analysis
   */
  async getEnhancedConversation(conversationId: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}${this.baseUrl}/conversations/revolutionary/${conversationId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}` || ''
      }
    });

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch enhanced conversation details');
    }

    return result.data;
  }

  /**
   * Get all conversations with enhanced filtering
   */
  async getAllConversations(filters: {
    status?: string;
    priority?: string;
    search?: string;
    limit?: number;
    offset?: number;
  } = {}): Promise<any> {
    const queryParams = new URLSearchParams();
    
    if (filters.status && filters.status !== 'all') queryParams.append('status', filters.status);
    if (filters.priority) queryParams.append('priority', filters.priority);
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.limit) queryParams.append('limit', filters.limit.toString());
    if (filters.offset) queryParams.append('offset', filters.offset.toString());

    const response = await fetch(`${API_BASE_URL}${this.baseUrl}/conversations?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}` || ''
      }
    });

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch conversations');
    }

    return result.data;
  }

  /**
   * Set conversation status
   */
  async setConversationStatus(conversationId: string, status: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}${this.baseUrl}/conversations/${conversationId}/status`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}` || ''
      },
      body: JSON.stringify({ status })
    });

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to update conversation status');
    }

    return result.data;
  }

  /**
   * Star/unstar conversation
   */
  async toggleConversationStar(conversationId: string, isStarred: boolean): Promise<any> {
    const response = await fetch(`${API_BASE_URL}${this.baseUrl}/conversations/${conversationId}/star`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}` || ''
      },
      body: JSON.stringify({ isStarred })
    });

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to update conversation star status');
    }

    return result.data;
  }
}

export const conversationService = new ConversationService();
export default conversationService; 